import React, { FC, useCallback, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import classNames from "classnames";
import { Notification } from "@app/components";
import { Svg } from "@natera/material/lib/svg";
import { stripHtmlTags } from "@app/utils/common";
import { BloodDrawSite } from "@app/provider";
import Arrow from "@assets/svg/icons/lab-arrow.svg";
import PhoneSvg from "@assets/svg/icons/labPhone.svg";
import ScheduleSvg from "@assets/svg/icons/labSchedule.svg";

import "./labItem.scss";

const messages = defineMessages({
  labCollectionLabListLabItemDistance: {
    id: "labCollectionLabListLabItemDistance",
    defaultMessage: "{distance} mi away",
  },
  labCollectionLabListLabItemClosed: {
    id: "labCollectionLabListLabItemClosed",
    defaultMessage: "Closed",
  },
  labCollectionLabListLabItemExpand: {
    id: "labCollectionLabListLabItemExpand",
    defaultMessage: "Expand",
  },
  labCollectionLabListLabItemCollapse: {
    id: "labCollectionLabListLabItemCollapse",
    defaultMessage: "Collapse",
  },
  scheduleAppointmentButton: {
    id: "scheduleAppointmentButton",
    defaultMessage: "Schedule appointment",
  },
});

interface LabItemProps {
  bloodDrawSite: BloodDrawSite;
  /**
   * When true, render the "Schedule appointment" out-link (if the lab
   * exposes an `appointment_scheduling_url`) plus the lab's comments note.
   * Renamed from `isPublicMpFlow` because all three MP flows (Public,
   * Guest, Logged-In) now render the link — UPP-5179/5178/5159.
   */
  showScheduleLink?: boolean;
}

const LabItem: FC<LabItemProps> = ({ bloodDrawSite, showScheduleLink }) => {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const { distance, name, full_address, phone, site_hours } = bloodDrawSite;

  const toggle = useCallback(() => {
    setIsOpen((value) => !value);
  }, [isOpen]);

  const contactLinks = () => {
    const showSchedule =
      showScheduleLink && !!bloodDrawSite?.appointment_scheduling_url;
    if (!phone && !showSchedule) return null;
    return (
      <div className="lab-item__details__links">
        {phone && (
          <a
            href={`tel:${phone}`}
            className="lab-item__details__links__link"
            data-testid="lab-item-phone-link"
          >
            <span className="lab-item__details__links__link__icon">
              <Svg content={PhoneSvg} />
            </span>
            <span className="lab-item__details__links__link__text">
              {phone}
            </span>
          </a>
        )}
        {showSchedule && (
          <a
            href={bloodDrawSite.appointment_scheduling_url}
            target="_blank"
            rel="noopener noreferrer"
            className="lab-item__details__links__link"
            data-testid="lab-item-schedule-link"
          >
            <span className="lab-item__details__links__link__icon">
              <Svg content={ScheduleSvg} />
            </span>
            <span className="lab-item__details__links__link__text">
              {intl.formatMessage(messages.scheduleAppointmentButton)}
            </span>
          </a>
        )}
      </div>
    );
  };

  const commentsNote = () => {
    if (!bloodDrawSite?.comments) return null;
    return (
      <Notification className={"lab-item__info__note"} type="note">
        <b>{stripHtmlTags(bloodDrawSite.comments)}</b>
      </Notification>
    );
  };

  return (
    <div className="lab-item">
      <button
        type="button"
        className="lab-item__header"
        aria-expanded={isOpen}
        aria-label={intl.formatMessage(
          isOpen
            ? messages.labCollectionLabListLabItemCollapse
            : messages.labCollectionLabListLabItemExpand
        )}
        onClick={toggle}
      >
        <div
          className={classNames("lab-item__chevron", {
            "lab-item__chevron--open": isOpen,
          })}
        >
          <Svg content={Arrow} />
        </div>

        <div className="lab-item__info">
          <div className="lab-item__info__distance">
            {intl.formatMessage(messages.labCollectionLabListLabItemDistance, {
              distance: distance.toFixed(0),
            })}
          </div>

          <div className="lab-item__info__name">{name}</div>

          <div className="lab-item__info__fulladdress">{full_address}</div>
        </div>
      </button>

      <div
        className={classNames("lab-item__details", {
          "lab-item__details--visible": isOpen,
        })}
        // Remove the collapsed subtree from the tab order + AT interaction
        // while keeping the max-height CSS transition intact. Follows the
        // WAI-ARIA disclosure pattern — Tab flows naturally into the
        // revealed content when the user expands.
        //
        // `inert` is a presence attribute: any non-null value (including
        // the literal string "false") activates it. React 18 doesn't
        // serialize `inert={false}` as omitted — it renders `inert="false"`
        // and the element stays inert. So we add/remove the attribute
        // conditionally via spread. The `as never` cast bypasses React
        // 18's DOM type that declares `inert: boolean`; runtime treats
        // this attribute as a string.
        {...((!isOpen ? { inert: "" } : {}) as Record<string, unknown>)}
      >
        {contactLinks()}

        <div className="lab-item__details__hours">
          {site_hours.map((siteHour) => (
            <div className="site-hour" key={siteHour.id}>
              <span className="site-hour__day">{siteHour.day_of_week}</span>

              <span className="site-hour__time">
                {!siteHour.close && !siteHour.open
                  ? intl.formatMessage(
                      messages.labCollectionLabListLabItemClosed
                    )
                  : `${siteHour.open} - ${siteHour.close}`}
              </span>
            </div>
          ))}
        </div>

        {showScheduleLink && commentsNote()}
      </div>
    </div>
  );
};

export default LabItem;
